export interface Crumb {
  readonly label: string;
  /** Ancestor crumbs link; the current (last) crumb omits `to`. */
  readonly to?: string;
}

const SETTINGS_LABELS: Record<string, string> = {
  retention: 'Retention settings',
  members: 'Members',
  'llm-key': 'LLM key',
  integrations: 'Integrations',
};

/** Sub-routes under `/projects/:id`, matched against the path AFTER the id. */
const PROJECT_ROUTES: readonly { re: RegExp; crumbs: (id: string) => Crumb[] }[] = [
  { re: /^\/edit$/, crumbs: () => [{ label: 'Edit project' }] },
  { re: /^\/domain-verification$/, crumbs: () => [{ label: 'Domain verification' }] },
  { re: /^\/scans$/, crumbs: () => [{ label: 'Scans' }] },
  {
    re: /^\/scans\/[^/]+\/discovery$/,
    crumbs: (id) => [{ label: 'Scans', to: `/projects/${id}/scans` }, { label: 'Scan discovery' }],
  },
  { re: /^\/journey-health$/, crumbs: () => [{ label: 'Journey health' }] },
  { re: /^\/journeys$/, crumbs: () => [{ label: 'Journeys' }] },
  {
    re: /^\/journeys\/new$/,
    crumbs: (id) => [{ label: 'Journeys', to: `/projects/${id}/journeys` }, { label: 'New journey' }],
  },
  {
    re: /^\/journeys\/[^/]+\/edit$/,
    crumbs: (id) => [{ label: 'Journeys', to: `/projects/${id}/journeys` }, { label: 'Edit journey' }],
  },
  {
    re: /^\/journeys\/[^/]+$/,
    crumbs: (id) => [{ label: 'Journeys', to: `/projects/${id}/journeys` }, { label: 'Journey' }],
  },
  { re: /^\/defects$/, crumbs: () => [{ label: 'Defects' }] },
  {
    re: /^\/defects\/[^/]+$/,
    crumbs: (id) => [{ label: 'Defects', to: `/projects/${id}/defects` }, { label: 'Defect' }],
  },
  { re: /^\/test-cases$/, crumbs: () => [{ label: 'Test cases' }] },
  { re: /^\/tracker-settings$/, crumbs: () => [{ label: 'Tracker settings' }] },
  { re: /^\/connected-validation$/, crumbs: () => [{ label: 'Connected validation' }] },
];

/**
 * Derive a breadcrumb trail from a pathname. Returns `[]` for the home page and
 * any route that has no meaningful ancestry (auth screens, unknown paths), so the
 * component can render nothing there. The last crumb is always the current page.
 *
 * When `projectName` is known (the active project is loaded), a project-context
 * crumb is inserted after Home on project routes so the current project is visible
 * on every page (#434); it links to the project's journey-health dashboard.
 */
export function buildCrumbs(pathname: string, projectName?: string | null): Crumb[] {
  const home: Crumb = { label: 'Home', to: '/' };

  const settings = /^\/settings\/([^/]+)$/.exec(pathname);
  if (settings) return [home, { label: SETTINGS_LABELS[settings[1] ?? ''] ?? 'Settings' }];

  if (pathname === '/projects/new') return [home, { label: 'New project' }];

  const proj = /^\/projects\/([^/]+)(\/.*)?$/.exec(pathname);
  const id = proj?.[1];
  if (id && id !== 'new') {
    const rest = proj?.[2] ?? '';
    const match = PROJECT_ROUTES.find((r) => r.re.test(rest));
    const crumbs: Crumb[] = [home];
    if (projectName) crumbs.push({ label: projectName, to: `/projects/${id}/journey-health` });
    const section = match ? match.crumbs(id) : projectName ? [] : [{ label: 'Project' }];
    crumbs.push(...section);
    return crumbs;
  }

  return [];
}
